import { useState, useRef, useEffect } from 'react';
import { Input, Button, notification, Form, Modal, message } from 'antd';
import {
	MessageSquare,
	Check,
	Settings,
	PlayCircle,
	FileText,
	MessageCircle,
	Phone,
	Calendar,
	HelpCircle,
	Webhook,
	Code,
	Share,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getConfigurationSuggestions, applyConfigurationSuggestion, getUser } from '@/api/user';
import PhoneWorkflowForm from '@/pages/ReceptionistDashboard/Overview/Cards/PhoneWorkflow/PhoneWorkflowForm';
import TextingWorkflowForm from '@/pages/ReceptionistDashboard/Overview/Cards/TextingWorkflow/TextingWorkflowForm';
import CallTransferingWorkflowFormEdit from '@/pages/ReceptionistDashboard/Overview/Cards/CallTransferingWorkflow/CallTransferingWorkflowFormEdit';
import { User } from '@/types/TAccounts';
import { TextingWorkflowFormRef } from '@/pages/ReceptionistDashboard/Overview/Cards/TextingWorkflow/TextingWorkflowForm';
import { PhoneWorkflowFormRef } from '@/pages/ReceptionistDashboard/Overview/Cards/PhoneWorkflow/PhoneWorkflowForm';
import { CallTransferingWorkflowFormRef } from '@/pages/ReceptionistDashboard/Overview/Cards/CallTransferingWorkflow/CallTransferingWorkflowFormEdit';
import './AICustomizationStep.scss';
import {
	getSelectedAgent,
	getSelectedAgentId,
	getResellerUserFeaturesGating,
	getResellerUserFeaturesVisibility,
	getLoggedInUser,
} from '@/store/account/selector';
import OverviewCard from '../../Overview/Cards/OverviewCard';
import { RocketOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { getResellerData } from '@/store/reseller/selector';
import { OverViewCards } from 'src/enums/EOverViewCards';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import ConfigurationCard from './ConfigurationCard';
import WorkflowIssuesFloatingButtons from '@/components/WorkflowEnhance/WorkflowIssuesFloatingButtons';
import Header from '@/components/Header';
import { getCalendars } from '@/api/calendar';
import CopilotSider from './CopilotSider';
import {
	SuggestionConfiguration,
	ApiConfigurationSuggestionsResponse,
	SuggestionPayload,
	PhoneWorkflowFormValues,
	CallWorkflowFormValues,
} from '@/types/suggestions';
import { isObject } from 'lodash';
import { FormActionType } from 'src/enums/EWorkflow';
import { safeJSONParse } from '@/utils/helper';
import { copyToClipboard } from '@/utils/helper';
import { CopyOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';

const MAX_GENERATIONS = 40;

interface CallWorkflow {
	description: string;
	voiceResponse: string;
	workflowForwardingNumber: string;
	whisperType: string;
	handoffMessageType: string;
	handoffMessage: string;
	callTransferee: string;
	displayedNumber: string;
	id: string;
	availability: any;
}

interface ConfiguredAction {
	type: string;
	icon: JSX.Element;
	count: number;
	items: string[];
}

const itemVariants = {
	hidden: { opacity: 0, x: -20 },
	visible: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: 20 },
};

const AICustomizationStep = ({
	onTest,
	showTestBtn = true,
	debrand,
}: {
	onTest?: () => void;
	showTestBtn?: boolean;
	debrand: boolean;
	isNoviUser?: boolean;
}) => {
	const [form] = Form.useForm();
	const [suggestions, setSuggestions] = useState<SuggestionConfiguration[]>([]);
	const [hasAppliedChanges, setHasAppliedChanges] = useState(false);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const [generationsLeft, setGenerationsLeft] = useState(
		MAX_GENERATIONS - (selectedAgent?.suggestion_count || 0),
	);
	const [displayMessage, setDisplayMessage] = useState<string>('');

	const [openCopilot, setOpenCopilot] = useState<boolean>(false);
	const phoneWorkflowFormRef = useRef<PhoneWorkflowFormRef>(null);
	const textingWorkflowFormRef = useRef<TextingWorkflowFormRef>(null);
	const callWorkflowFormRef = useRef<CallTransferingWorkflowFormRef>(null);
	const clientHiddenFeatures = useAppSelector(getResellerUserFeaturesVisibility) || [];
	const clientFeaturesGating = useAppSelector(getResellerUserFeaturesGating) || [];
	const currentReseller = useAppSelector(getResellerData) as { resellerView: boolean };
	const loggedInUser = useAppSelector(getLoggedInUser);
	const dispatch = useAppDispatch();

	const { data: calendarData } = useQuery({
		queryKey: ['get-calendar', selectedAgentId],
		queryFn: () => getCalendars(selectedAgentId),
		enabled: !!selectedAgentId,
	});

	const { refetch } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId),
		enabled: !!selectedAgentId,
	});

	// Check generation limit on component mount and when loggedInUser changes
	useEffect(() => {
		if (selectedAgent?.suggestion_count !== undefined) {
			const remaining = MAX_GENERATIONS - selectedAgent.suggestion_count;
			setGenerationsLeft(remaining);

			// Remove Copilot button and ensure its closed if No tokens or Copilot is hidden
			if (
				remaining <= 0 ||
				(clientHiddenFeatures.includes('Copilot') && !currentReseller?.resellerView)
			) {
				setOpenCopilot(false);
			}
		}
	}, [selectedAgent?.suggestion_count, clientHiddenFeatures, currentReseller?.resellerView]);

	const getSuggestions = useMutation({
		mutationKey: ['getSuggestions'],
		mutationFn: async (payload: { query: string; agentId: string }) => {
			const response = await getConfigurationSuggestions(payload);
			return response as ApiConfigurationSuggestionsResponse;
		},
		onSuccess: (data: ApiConfigurationSuggestionsResponse) => {
			if (data.success) {
				const convertedSuggestions = data?.suggestions?.suggestions
					.map((suggestion) => {
						const { type, value, isUpdate, targetId } = suggestion;
						switch (type) {
							case 'ai_prompt':
							case 'system_phrase':
								return {
									type,
									value: value as string,
									isUpdate: isUpdate || false,
									targetId: targetId || '',
								} as SuggestionConfiguration;
							case 'intake_questions':
								return {
									type,
									value: value as string[],
									isUpdate: isUpdate || false,
									targetId: targetId || '',
								} as SuggestionConfiguration;
							case 'intake_questions_workflows':
								return {
									type,
									value: value as Array<{
										customScenario: string;
										questions: string[];
										postExecutionPrompt: string;
									}>,
									isUpdate: isUpdate || false,
									targetId: targetId || '',
								} as SuggestionConfiguration;
							case 'knowledgeBase':
								return {
									type,
									value: value as Array<{
										type: string;
										content: string;
										isActive: boolean;
										status: string;
									}>,
									isUpdate: isUpdate || false,
									targetId: targetId || '',
								} as SuggestionConfiguration;
							case 'workflows':
								return {
									type,
									value: value as Array<{
										description: string;
										textMessage: string;
										shortURLEnabled: boolean;
									}>,
									isUpdate: isUpdate || false,
									targetId: targetId || '',
								} as SuggestionConfiguration;
							case 'callWorkflows':
								return {
									type,
									value: value as Array<{
										description: string;
										voiceResponse: string;
										workflowForwardingNumber: string;
										id: string;
									}>,
									isUpdate: isUpdate || false,
									targetId: targetId || '',
								} as SuggestionConfiguration;
							default:
								return null;
						}
					})
					.filter((s): s is SuggestionConfiguration => s !== null);
				setSuggestions(convertedSuggestions);

				// Only set message if it's a non-empty string
				const message = data.suggestions.message;
				if (message && typeof message === 'string' && message.trim() !== '') {
					setDisplayMessage(message);
				}

				if (typeof data.remaining_suggestions === 'number') {
					setGenerationsLeft(data.remaining_suggestions);
				}
			} else {
				notification.error({
					message: data.suggestions.message || 'Failed to get suggestions',
				});
			}
		},
		onError: (error: any) => {
			notification.error({
				message: error.message || 'Failed to get suggestions',
			});
		},
	});

	const applySuggestion = useMutation({
		mutationKey: ['applySuggestion'],
		mutationFn: async (suggestion: SuggestionConfiguration) => {
			const response = await applyConfigurationSuggestion({
				agentId: selectedAgentId as string,
				suggestion: {
					type: suggestion.type,
					value: suggestion.value,
					isUpdate: suggestion.isUpdate,
					targetId: suggestion.targetId,
				} as SuggestionPayload,
			});
			return response;
		},
		onSuccess: (data) => {
			if (data.success) {
				notification.success({
					message: 'Changes applied successfully',
					description: data.message || 'Your configuration has been updated.',
				});
				setSuggestions([]);
				setHasAppliedChanges(true);
				form.resetFields();
				refetch();
			} else {
				notification.error({
					message: 'Failed to apply changes',
					description: data.message || 'Please try again.',
				});
			}
		},
		onError: (error: any) => {
			notification.error({
				message: 'Failed to apply changes',
				description: error.message || 'An error occurred while applying the changes.',
			});
		},
	});

	// -------------- Copilot Sider handle submit
	const handleSubmit = async () => {
		const values = await form.validateFields();
		if (!values.prompt) {
			notification.error({
				message: 'Please enter a prompt',
			});
			return;
		}

		if (generationsLeft <= 0) {
			notification.error({
				message: 'You have reached the maximum number of generations',
			});
			return;
		}

		// Check if Copilot is gated and not in reseller view
		if (clientFeaturesGating.includes('Copilot') && !currentReseller?.resellerView) {
			dispatch(openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }));
			return;
		}

		setHasAppliedChanges(false);
		getSuggestions.mutate({ query: values.prompt, agentId: selectedAgentId as string });
	};

	const getLatestFormValues = async (
		suggestion: SuggestionConfiguration,
	): Promise<SuggestionConfiguration> => {
		try {
			let updatedValue = suggestion.value;

			switch (suggestion.type) {
				case 'intake_questions':
				case 'intake_questions_workflows':
					if (phoneWorkflowFormRef.current?.form) {
						try {
							const values =
								(await phoneWorkflowFormRef.current.form.validateFields()) as PhoneWorkflowFormValues;
							if (suggestion.type === 'intake_questions') {
								// For beginning scenario, extract questions from the form values
								const questions = values.scenarios[0]?.questions?.map((q) => q.question) || [];
								updatedValue = questions;
							} else {
								// For custom scenarios, map the workflow values
								updatedValue = values.scenarios.map((scenario) => ({
									customScenario: scenario.customScenario || '',
									questions: scenario.questions?.map((q) => q.question) || [],
									postExecutionPrompt: scenario.beginning_question_final_phrase || '',
									description: 'custom-scenario',
								}));
							}
						} catch (validationError) {
							console.error('Form validation error:', validationError);
							throw validationError;
						}
					}
					break;

				case 'workflows':
					if (textingWorkflowFormRef.current) {
						try {
							// Get the form values directly from the form instance
							const values = await textingWorkflowFormRef.current.form.validateFields();

							if (values?.workflows) {
								updatedValue = values.workflows.map((workflow: any) => ({
									description: workflow.description || '',
									textMessage: workflow.textMessage || '',
									shortURLEnabled: workflow.shortURLEnabled ?? true,
									postExecutionPrompt: workflow.postExecutionPrompt || '',
								}));
							} else {
								console.warn('No form values found, using original suggestion value');
								updatedValue = suggestion.value;
							}
						} catch (validationError) {
							console.error('Form validation error:', validationError);
							throw validationError;
						}
					}
					break;

				case 'callWorkflows':
					if (callWorkflowFormRef.current?.form) {
						try {
							const values =
								(await callWorkflowFormRef.current.form.validateFields()) as CallWorkflowFormValues;
							updatedValue = [
								{
									description: values.description || '',
									voiceResponse: values.voiceResponse || '',
									workflowForwardingNumber: values.workflowForwardingNumber
										? values.workflowForwardingNumber.startsWith('+')
											? values.workflowForwardingNumber
											: `+${values.workflowForwardingNumber}`
										: '',
									id: values.id || '',
								},
							];
						} catch (validationError) {
							console.error('Form validation error:', validationError);
							throw validationError;
						}
					}
					break;

				case 'knowledgeBase':
				case 'ai_prompt':
				case 'system_phrase':
					// For these types, we don't need to get updated values as they are read-only
					break;
			}

			return {
				type: suggestion.type,
				value: updatedValue,
				isUpdate: suggestion.isUpdate,
				targetId: suggestion.targetId,
			} as SuggestionConfiguration;
		} catch (error) {
			console.error('Error getting latest form values:', error);
			throw error;
		}
	};

	const handleAcceptSuggestion = async () => {
		if (!suggestions.length) {
			notification.warning({
				message: 'No suggestion available',
				description: 'Please generate a suggestion first.',
			});
			return;
		}

		try {
			const suggestion = suggestions[0]; // Always use the first suggestion
			const updatedSuggestion = await getLatestFormValues(suggestion);
			applySuggestion.mutate(updatedSuggestion);
		} catch (error: any) {
			notification.error({
				message: 'Validation Error',
				description: error.message || 'Please check the form values and try again.',
			});
		}
	};

	const handleRejectSuggestions = () => {
		setSuggestions([]);
		setDisplayMessage('');
		form.resetFields();
	};

	const renderSuggestionContent = (suggestion: SuggestionConfiguration) => {
		if (!suggestion || !suggestion.type) {
			return null;
		}
		switch (suggestion.type) {
			case 'intake_questions': {
				if (!Array.isArray(suggestion.value)) {
					return null;
				}
				const intakePhoneWorkflow = {
					scenarios: [
						{
							description: 'beginning',
							customScenario: '',
							questions: suggestion.value.map((question) => ({
								question,
							})),
							beginning_question_final_phrase: '',
							intake_questions: suggestion.value,
							intake_questions_workflows: [],
							previousWorkflowData: [],
						},
					],
				};
				return (
					<div>
						<PhoneWorkflowForm
							ref={phoneWorkflowFormRef}
							initialValues={intakePhoneWorkflow}
							showAddFieldButton={false}
							refetch={async () => ({ data: {} as User }) as any}
							mutation={getSuggestions as any}
							actionType={FormActionType.EDIT}
							disableSubmittion
						/>
					</div>
				);
			}
			case 'intake_questions_workflows': {
				if (!Array.isArray(suggestion.value)) {
					return null;
				}
				const workflowPhoneWorkflows = suggestion.value.map((workflow) => ({
					description: 'custom-scenario',
					customScenario: workflow.customScenario,
					questions: workflow.questions.map((q) => ({ question: q })),
					beginning_question_final_phrase: workflow.postExecutionPrompt,
					intake_questions: workflow.questions,
					intake_questions_workflows: [
						{
							customScenario: workflow.customScenario,
							questions: workflow.questions,
							postExecutionPrompt: workflow.postExecutionPrompt,
						},
					],
					previousWorkflowData: [],
					postExecutionPrompt: workflow.postExecutionPrompt,
				}));
				return (
					<div>
						<PhoneWorkflowForm
							ref={phoneWorkflowFormRef}
							initialValues={{ scenarios: workflowPhoneWorkflows }}
							showAddFieldButton={false}
							refetch={async () => ({ data: {} as User }) as any}
							mutation={getSuggestions as any}
							actionType={FormActionType.EDIT}
							disableSubmittion
						/>
					</div>
				);
			}
			case 'workflows':
				if (!Array.isArray(suggestion.value)) {
					return null;
				}
				return (
					<div>
						<TextingWorkflowForm
							ref={textingWorkflowFormRef}
							initialValues={{
								workflows: suggestion.value.map((workflow) => ({
									description: workflow.description,
									textMessage: workflow.textMessage,
									postExecutionPrompt: workflow.postExecutionPrompt || '',
									shortURLEnabled: workflow.shortURLEnabled,
								})),
							}}
							showAddFieldButton={false}
							refetch={async () => ({ data: {} as User }) as any}
							mutation={getSuggestions as any}
							actionType={FormActionType.EDIT}
							debrand={debrand}
							isNoviUser={false}
							disableSubmittion
						/>
					</div>
				);
			case 'callWorkflows': {
				if (!Array.isArray(suggestion.value)) {
					return null;
				}
				const callWorkflow: CallWorkflow = {
					...suggestion.value[0],
					whisperType: '',
					handoffMessageType: '',
					handoffMessage: '',
					callTransferee: '',
					displayedNumber: '',
					availability: null,
				};
				return (
					<div>
						<CallTransferingWorkflowFormEdit
							ref={callWorkflowFormRef}
							callWorkflow={callWorkflow}
							callTransferingWorkflowData={
								suggestion.value.map((w) => ({
									...w,
									whisperType: '',
									handoffMessageType: '',
									handoffMessage: '',
									callTransferee: '',
									displayedNumber: '',
									availability: null,
								})) as CallWorkflow[]
							}
							refetch={async () => ({ data: {} as User }) as any}
							mutation={getSuggestions as any}
							scenarioIndex={0}
							disableSubmittion
						/>
					</div>
				);
			}
			case 'knowledgeBase': {
				if (!Array.isArray(suggestion.value) || !suggestion.value[0]) {
					return null;
				}
				const content = suggestion.value[0].content || '';
				return (
					<Form.Item
						className="p-2"
						label={<div className="font-bold p-2">Knowledge Base Content</div>}
					>
						<Input.TextArea value={content} autoSize={{ minRows: 3, maxRows: 10 }} readOnly />
					</Form.Item>
				);
			}
			case 'ai_prompt':
			case 'system_phrase':
				if (typeof suggestion.value !== 'string') {
					return null;
				}
				return (
					<Form.Item
						className="p-2"
						label={
							<div className="font-bold p-2">
								{suggestion.type === 'ai_prompt' ? 'AI Prompt' : 'System Phrase'}
							</div>
						}
					>
						<Input.TextArea
							value={suggestion.value}
							autoSize={{ minRows: 3, maxRows: 10 }}
							readOnly
						/>
					</Form.Item>
				);
			default:
				return <>No content!</>;
		}
	};

	const LoadingAnimation = () => (
		<motion.div className="loading-suggestions">
			<motion.div
				className="loading-circle"
				animate={{
					scale: [1, 1.2, 1],
					opacity: [0.3, 0.8, 0.3],
				}}
				transition={{
					duration: 1.5,
					repeat: Infinity,
					ease: 'easeInOut',
				}}
			>
				<MessageSquare size={24} />
			</motion.div>
			<motion.div
				className="loading-bars"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.3 }}
			>
				{[1, 2, 3].map((i) => (
					<motion.div
						key={i}
						className="loading-bar"
						initial={{ width: '60%', opacity: 0.3 }}
						animate={{
							width: ['60%', '100%', '80%', '60%'],
							opacity: [0.3, 0.6, 0.3],
						}}
						transition={{
							duration: 2,
							repeat: Infinity,
							delay: i * 0.2,
							ease: 'easeInOut',
						}}
					/>
				))}
			</motion.div>
		</motion.div>
	);

	const TypingAnimation = ({ text }: { text: string }) => {
		const [displayText, setDisplayText] = useState('');
		const [hasRun, setHasRun] = useState(false);
		const textRef = useRef(text);
		const hasEverRunRef = useRef(false);

		useEffect(() => {
			// Only reset and reanimate if the text actually changes
			if (text !== textRef.current) {
				setDisplayText('');
				setHasRun(false);
				hasEverRunRef.current = false;
				textRef.current = text;
			}

			// Don't animate if text is empty, has run before, or has ever completed
			if (!text || hasRun || hasEverRunRef.current) {
				return;
			}

			let index = 0;
			const timer = setInterval(() => {
				if (index < text.length) {
					setDisplayText(text.substring(0, index + 1));
					index++;
				} else {
					clearInterval(timer);
					setHasRun(true);
					hasEverRunRef.current = true;
				}
			}, 30);

			return () => clearInterval(timer);
		}, [text, hasRun]);

		// Don't render anything if there's no text
		if (!text) {
			return null;
		}

		return (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				className="text-2xl font-semibold text-gray-800"
			>
				{hasRun || hasEverRunRef.current ? text : displayText}
			</motion.div>
		);
	};

	const renderSuggestionItem = (suggestion: SuggestionConfiguration) => (
		<motion.div
			variants={itemVariants}
			initial="hidden"
			animate="visible"
			exit="exit"
			transition={{ duration: 0.2, ease: 'easeOut' }}
			style={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: '12px',
				padding: '8px 12px',
				backgroundColor: '#f8fafc',
				borderRadius: '12px',
				border: '1px solid #e2e8f0',
			}}
		>
			<div className="suggestion-content">{renderSuggestionContent(suggestion)}</div>
		</motion.div>
	);

	// Function to handle card clicks and open corresponding sections
	const handleCardClick = (cardType: string) => {
		// Prevent event propagation
		event?.stopPropagation();

		// First scroll to the OverviewCard
		const overviewCardElement = document.querySelector('.overview-card-container');
		if (overviewCardElement) {
			overviewCardElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}

		// Wait for scroll to complete before triggering the appropriate section
		setTimeout(() => {
			switch (cardType) {
				case 'Greeting Phrase':
					// Open Basic Settings parent panel first
					openParentPanel('basic-settings');
					// Use a longer delay to ensure parent panel is fully opened
					setTimeout(() => {
						// Then open the specific Greeting Phrase child panel
						openChildPanel(OverViewCards.GREETING_PHRASE);
					}, 500);
					break;
				case 'Commands':
					// Open Basic Settings parent panel first
					openParentPanel('basic-settings');
					setTimeout(() => {
						// Then open the specific AI Prompt child panel
						openChildPanel(OverViewCards.AI_PROMPT);
					}, 500);
					break;
				case 'Business Info':
					// Open Basic Settings parent panel first
					openParentPanel('basic-settings');
					setTimeout(() => {
						// Then open the specific Knowledge Base child panel
						openChildPanel(OverViewCards.KNOWLEDGE_BASE);
					}, 500);
					break;
				case 'Voice':
					// This one works with modal
					dispatch(setCurrentPage(ReceptionistDashboardSiderItem.VOICE_LIBRARY));
					break;
				case 'Transfer Call':
					// Open In-Call Actions parent panel first
					openParentPanel('during-call-actions');
					setTimeout(() => {
						// Then open the specific Call Transfer child panel
						openChildPanel(OverViewCards.CALL_TRANSFERRING_WORKFLOW);
					}, 500);
					break;
				case 'Send Text':
					// Open In-Call Actions parent panel first
					openParentPanel('during-call-actions');
					setTimeout(() => {
						// Then open the specific Call Transfer child panel
						openChildPanel(OverViewCards.TEXTING_WORKFLOW);
					}, 500);
					break;
				case 'Schedule an Appointment':
					// Open In-Call Actions parent panel first
					openParentPanel('during-call-actions');

					// Use a longer delay to ensure parent panel is fully opened
					setTimeout(() => {
						console.log('Opening calendar section after delay');
						// Try multiple approaches to find and open the calendar section
						const selectors = [
							`.child-accordion .ant-collapse-item[data-key="${OverViewCards.CALENDAR_SCHEDULING_WORKFLOW}"] .ant-collapse-header`,
							`.child-accordion .ant-collapse-item .ant-collapse-header:has([key="${OverViewCards.CALENDAR_SCHEDULING_WORKFLOW}"])`,
							'.child-accordion .ant-collapse-header:has([title*="Schedule an Appointment"])',
						];

						let found = false;
						for (const selector of selectors) {
							const calendarSection = document.querySelector(selector);
							if (calendarSection) {
								const isOpen = calendarSection
									.closest('.ant-collapse-item')
									?.classList.contains('ant-collapse-item-active');
								if (!isOpen) {
									console.log('Clicking calendar section');
									(calendarSection as HTMLElement).click();
								}
								found = true;
								break;
							}
						}

						if (!found) {
							const childHeaders = Array.from(
								document.querySelectorAll('.child-accordion .ant-collapse-header'),
							);
							for (const header of childHeaders) {
								const headerText = header.textContent || '';
								if (headerText.includes('Schedule an Appointment')) {
									const isOpen = header
										.closest('.ant-collapse-item')
										?.classList.contains('ant-collapse-item-active');
									if (!isOpen) {
										console.log('Clicking calendar section found by text content');
										(header as HTMLElement).click();
									}
									break;
								}
							}
						}
					}, 1000); // Increased delay to 1000ms
					break;
				case 'Common Questions':
					// Open Basic Settings parent panel first
					openParentPanel('basic-settings');
					setTimeout(() => {
						// Then open the specific Common Questions child panel
						openChildPanel(OverViewCards.SAMPLE_QUESTIONS_ANSWERS);
					}, 500);
					break;
				case 'Intake Questions':
					openParentPanel('during-call-actions');
					setTimeout(() => {
						openChildPanel(OverViewCards.PHONE_FORM_WORKFLOW);
					}, 500);
					break;
				case 'Post Call Webhook':
					openParentPanel('developer-automations');
					setTimeout(() => {
						openChildPanel(OverViewCards.POST_CALL_WEBHOOK);
					}, 500);
					break;
				case 'API Integration':
					openParentPanel('developer-automations');
					setTimeout(() => {
						openChildPanel(OverViewCards.API_WORKFLOW);
					}, 500);
					break;
				default:
					console.log('No action configured for:', cardType);
			}
		}, 500);
	};

	// Helper function to open parent accordion panels
	const openParentPanel = (panelKey: string) => {
		const parentPanelHeader = document.querySelector(
			`.parent-accordion .ant-collapse-item[data-key="${panelKey}"] .ant-collapse-header, ` +
				`.parent-accordion .ant-collapse-item .ant-collapse-header:has([key="${panelKey}"])`,
		);

		if (!parentPanelHeader) {
			// Fallback: try to find by content
			const headers = Array.from(
				document.querySelectorAll('.parent-accordion .ant-collapse-header'),
			);
			for (const header of headers) {
				if (header.textContent?.includes('Basic Settings') && panelKey === 'basic-settings') {
					// Check if panel is already open
					const isOpen = header
						.closest('.ant-collapse-item')
						?.classList.contains('ant-collapse-item-active');
					if (!isOpen) {
						(header as HTMLElement).click();
					}
					return;
				}
				if (header.textContent?.includes('In-Call Actions') && panelKey === 'during-call-actions') {
					// Check if panel is already open
					const isOpen = header
						.closest('.ant-collapse-item')
						?.classList.contains('ant-collapse-item-active');
					if (!isOpen) {
						(header as HTMLElement).click();
					}
					return;
				}
				if (
					header.textContent?.includes('Developer Automations') &&
					panelKey === 'developer-automations'
				) {
					// Check if panel is already open
					const isOpen = header
						.closest('.ant-collapse-item')
						?.classList.contains('ant-collapse-item-active');
					if (!isOpen) {
						(header as HTMLElement).click();
					}
					return;
				}
			}
		} else {
			// Check if panel is already open
			const isOpen = parentPanelHeader
				.closest('.ant-collapse-item')
				?.classList.contains('ant-collapse-item-active');
			if (!isOpen) {
				(parentPanelHeader as HTMLElement).click();
			}
		}
	};

	// Helper function to open child accordion panels
	const openChildPanel = (cardKey: OverViewCards) => {
		// Try multiple approaches to find the child panel

		const selectors = [
			`.child-accordion .ant-collapse-item[data-key="${cardKey}"] .ant-collapse-header`,
			`.child-accordion .ant-collapse-item .ant-collapse-header:has([key="${cardKey}"])`,
			`.child-accordion .ant-collapse-header:has([title*="${cardKey}"])`,
		];

		let found = false;
		for (const selector of selectors) {
			const childPanelHeader = document.querySelector(selector);
			if (childPanelHeader) {
				// Check if panel is already open
				const isOpen = childPanelHeader
					.closest('.ant-collapse-item')
					?.classList.contains('ant-collapse-item-active');
				if (!isOpen) {
					(childPanelHeader as HTMLElement).click();
				}
				found = true;
				break;
			}
		}

		if (!found) {
			// Fallback: search by text content
			const childHeaders = Array.from(
				document.querySelectorAll('.child-accordion .ant-collapse-header'),
			);
			for (const header of childHeaders) {
				const headerText = header.textContent || '';
				if (
					(cardKey === OverViewCards.GREETING_PHRASE && headerText.includes('Greeting')) ||
					(cardKey === OverViewCards.AI_PROMPT && headerText.includes('Custom Commands')) ||
					(cardKey === OverViewCards.KNOWLEDGE_BASE &&
						headerText.includes('Business Information')) ||
					(cardKey === OverViewCards.CALL_TRANSFERRING_WORKFLOW &&
						headerText.includes('Transfer Call')) ||
					(cardKey === OverViewCards.TEXTING_WORKFLOW &&
						headerText.includes('Send Text Message')) ||
					(cardKey === OverViewCards.CALENDAR_SCHEDULING_WORKFLOW &&
						headerText.includes('Schedule an Appointment')) ||
					(cardKey === OverViewCards.SAMPLE_QUESTIONS_ANSWERS &&
						headerText.includes('Common Questions')) ||
					(cardKey === OverViewCards.PHONE_FORM_WORKFLOW &&
						headerText.includes('Collect Information')) ||
					(cardKey === OverViewCards.POST_CALL_WEBHOOK && headerText.includes('Custom Webhook')) ||
					(cardKey === OverViewCards.API_WORKFLOW && headerText.includes('API Integration'))
				) {
					// Check if panel is already open
					const isOpen = header
						.closest('.ant-collapse-item')
						?.classList.contains('ant-collapse-item-active');
					if (!isOpen) {
						(header as HTMLElement).click();
					}
					break;
				}
			}
		}
		setTimeout(() => {
			if (
				cardKey === OverViewCards.POST_CALL_WEBHOOK ||
				cardKey === OverViewCards.PHONE_FORM_WORKFLOW ||
				cardKey === OverViewCards.API_WORKFLOW
			) {
				const overviewCardElement = document.querySelector('.overview-card-container');
				if (overviewCardElement) {
					overviewCardElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
				}
			}
		}, 500);
	};

	// Get in-call actions data
	const configuredActions: ConfiguredAction[] = [];

	// Check for call transfer workflows
	try {
		const callWorkflows = selectedAgent?.callWorkflows
			? (() => {
					try {
						return safeJSONParse(selectedAgent?.callWorkflows, []);
					} catch (parseError) {
						console.error('Error parsing call workflows JSON:', parseError);
						return [];
					}
				})()
			: [];

		if (Array.isArray(callWorkflows) && callWorkflows.length > 0) {
			configuredActions.push({
				type: 'Transfer Call',
				icon: <Phone size={16} />,
				count: callWorkflows.length,
				items: callWorkflows.map((wf: any) => {
					try {
						return wf.description || 'Untitled workflow';
					} catch (error) {
						console.error('Error processing workflow description:', error);
						return 'Error processing workflow';
					}
				}),
			});
		}
	} catch (error) {
		console.error('Error processing call workflows:', error);
	}

	// Check for texting workflows
	try {
		const textingWorkflows = selectedAgent?.workflows
			? (() => {
					try {
						return safeJSONParse(selectedAgent?.workflows, []);
					} catch (parseError) {
						console.error('Error parsing texting workflows JSON:', parseError);
						return [];
					}
				})()
			: [];

		if (Array.isArray(textingWorkflows) && textingWorkflows.length > 0) {
			configuredActions.push({
				type: 'Send Text',
				icon: <MessageCircle size={16} />,
				count: textingWorkflows.length,
				items: textingWorkflows.map((wf: any) => {
					try {
						return wf.description || 'Untitled workflow';
					} catch (error) {
						console.error('Error processing workflow description:', error);
						return 'Error processing workflow';
					}
				}),
			});
		}
	} catch (error) {
		console.error('Error processing texting workflows:', error);
	}

	// Check for phone form workflows (intake questions)
	try {
		const allIntakeItems = [];

		// Check basic intake questions
		const basicIntakeQuestions = selectedAgent?.intake_questions;
		if (basicIntakeQuestions) {
			try {
				const parsedBasicQuestions =
					typeof basicIntakeQuestions === 'string'
						? (() => {
								try {
									return safeJSONParse(basicIntakeQuestions, []);
								} catch (parseError) {
									console.error('Error parsing basic intake questions JSON:', parseError);
									return [];
								}
							})()
						: basicIntakeQuestions;

				if (Array.isArray(parsedBasicQuestions) && parsedBasicQuestions.length > 0) {
					allIntakeItems.push(
						...parsedBasicQuestions.map((item: any) => {
							try {
								return typeof item === 'string' ? item : JSON.stringify(item);
							} catch (error) {
								console.error('Error processing intake question:', error);
								return 'Error processing question';
							}
						}),
					);
				}
			} catch (error) {
				console.error('Error processing basic intake questions:', error);
				if (Array.isArray(basicIntakeQuestions) && basicIntakeQuestions.length > 0) {
					allIntakeItems.push(
						...basicIntakeQuestions.map((item: any) => {
							try {
								return typeof item === 'string' ? item : JSON.stringify(item);
							} catch (error) {
								console.error('Error processing intake question:', error);
								return 'Error processing question';
							}
						}),
					);
				}
			}
		}
	} catch (error) {
		console.error('Error processing phone form workflows:', error);
	}

	if (typeof window !== 'undefined' && !document.getElementById('ai-customization-pill-styles')) {
		const style = document.createElement('style');
		style.id = 'ai-customization-pill-styles';
		document.head.appendChild(style);
	}

	const handleMakeLive = () => {
		// TODO: Implement make live functionality
		if (loggedInUser.inboundDemo) {
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST));
		} else {
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.GET_STARTED));
		}
	};

	const handleShareReceptionist = () => {
		const url = `${import.meta.env.VITE_APP_BASE_URL}/share/${loggedInUser?.username}?source=share`;
		let inputRef = null;

		window.gtag('event', 'shareable_link_copied', {
			event_category: 'AI_Customization',
			event_label: 'Share My Amazing Receptionist',
		});

		posthog.capture('shareable_link_copied', {
			location: 'ai_customization_step',
			button_label: 'Share My Amazing Receptionist',
		});
		Modal.info({
			title: 'Share Your AI Receptionist',
			content: (
				<div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
					<div>Copy and share this link:</div>
					<div style={{ display: 'flex', gap: 8 }}>
						<Input
							value={url}
							readOnly
							ref={(ref) => (inputRef = ref)}
							style={{ flex: 1 }}
							onFocus={(e) => e.target.select()}
						/>
						<Button
							icon={<CopyOutlined />}
							onClick={() => {
								copyToClipboard(url);
								message.success('Link copied!');
								if (inputRef) {
									inputRef.focus();
								}
							}}
						>
							Copy
						</Button>
					</div>
				</div>
			),
			okText: 'Close',
			width: 480,
			maskClosable: true,
		});
	};

	const toggleDrawer = (newOpen: boolean) => {
		setOpenCopilot(newOpen);
	};

	return (
		<div className="ai-customization-container" style={{ backgroundColor: '#f8faff' }}>
			<Header
				debrand={loggedInUser.resellerClient || debrand}
				title="My AI Receptionist"
				description="Manage everything your AI Receptionist can do"
				icon={<CustomerServiceOutlined />}
			/>

			{/* Only show CopilotSider if generations are available and Copilot is not hidden */}
			{generationsLeft > 0 &&
				(!clientHiddenFeatures.includes('Copilot') || currentReseller?.resellerView) && (
					<CopilotSider
						open={openCopilot}
						toggleDrawer={toggleDrawer}
						form={form}
						handleSubmit={handleSubmit}
						getSuggestions={getSuggestions}
						applySuggestion={applySuggestion}
						handleAcceptSuggestion={handleAcceptSuggestion}
						handleRejectSuggestions={handleRejectSuggestions}
						suggestions={suggestions}
						displayMessage={displayMessage}
						renderSuggestionItem={renderSuggestionItem}
						hasAppliedChanges={hasAppliedChanges}
						showTestBtn={showTestBtn}
						onTest={onTest}
						TypingAnimation={TypingAnimation}
						LoadingAnimation={LoadingAnimation}
					/>
				)}
			<div className="px-4">
				<motion.div
					key="detailed"
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -20 }}
					transition={{ duration: 0.3 }}
					className="tab-content detailed-content ai-customization-content"
				>
					<div className="settings-container">
						<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 mb-6">
							<div className="flex items-center gap-3">
								<div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-2.5 rounded-xl shadow-lg shadow-emerald-500/20">
									<Check size={20} className="text-white" />
								</div>
								<h3 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight m-0">
									Overview
								</h3>
							</div>
							<div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
								<Button
									type="primary"
									size="large"
									icon={<RocketOutlined />}
									onClick={handleMakeLive}
									className="w-full sm:w-auto bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 
									border-none rounded-xl font-semibold h-12 px-4 sm:px-6 shadow-lg shadow-emerald-500/25 
									hover:shadow-xl hover:shadow-emerald-500/35 hover:-translate-y-0.5 transition-all duration-300
									relative overflow-hidden group text-sm sm:text-base"
								>
									<div
										className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent 
									opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
									/>
									<span className="relative z-10">Make My Receptionist Live!</span>
								</Button>
								{!loggedInUser?.resellerClient && (
									<Button
										type="default"
										size="large"
										icon={<Share className="w-4 h-4" />}
										onClick={handleShareReceptionist}
										className="w-full sm:w-auto border-2 border-orange-200 text-orange-600 hover:border-orange-300 hover:text-orange-700 
									rounded-xl font-semibold h-12 px-4 sm:px-6 shadow-lg hover:shadow-xl transition-all duration-300
									relative overflow-hidden group text-sm sm:text-base"
									>
										<span className="relative z-10">Share My Amazing Receptionist</span>
									</Button>
								)}
							</div>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
							{/* Basic Settings Cards */}
							{selectedAgent?.system_phrase && (
								<ConfigurationCard
									title="Greeting Phrase"
									icon={MessageSquare}
									iconColor="text-indigo-600"
									iconBgColor="bg-indigo-100"
									content={selectedAgent.system_phrase}
									onCardClick={() => handleCardClick('Greeting Phrase')}
									emptyDescription="No greeting phrase"
								/>
							)}

							{selectedAgent?.ai_prompt && (
								<ConfigurationCard
									title="Commands"
									icon={Settings}
									iconColor="text-green-600"
									iconBgColor="bg-green-100"
									content={selectedAgent.ai_prompt}
									onCardClick={() => handleCardClick('Commands')}
									emptyDescription="No commands"
								/>
							)}

							{selectedAgent?.knowledgeBase.length > 1 && (
								<ConfigurationCard
									title="Business Info"
									icon={FileText}
									iconColor="text-red-600"
									iconBgColor="bg-red-100"
									content={(() => {
										try {
											const parsedKnowledgeBase = isObject(selectedAgent.knowledgeBase)
												? selectedAgent.knowledgeBase
												: safeJSONParse(selectedAgent?.knowledgeBase, []);
											if (Array.isArray(parsedKnowledgeBase)) {
												return parsedKnowledgeBase
													.filter((item) => item.content && !item.structuredData)
													.map((item) => {
														const displayContent = item.content
															? item.content.replace(/<[^>]*>?/gm, '').substring(0, 150) +
																(item.content.length > 150 ? '...' : '')
															: '';
														return displayContent;
													})
													.filter((content) => content.trim() !== '');
											}
											return selectedAgent.knowledgeBase;
										} catch (error) {
											console.error('Error parsing knowledge base:', error);
											return selectedAgent.knowledgeBase;
										}
									})()}
									onCardClick={() => handleCardClick('Business Info')}
									emptyDescription="No business info"
									isArrayContent
								/>
							)}

							{/* In-Call Actions Cards */}
							{(() => {
								const transferAction = configuredActions.find(
									(action) => action.type === 'Transfer Call',
								);
								return transferAction && transferAction.items && transferAction.items.length > 0 ? (
									<ConfigurationCard
										title="Transfer Call"
										icon={Phone}
										iconColor="text-purple-600"
										iconBgColor="bg-purple-100"
										content={transferAction.items}
										onCardClick={() => handleCardClick('Transfer Call')}
										emptyDescription="No transfer workflows"
										isArrayContent
									/>
								) : null;
							})()}

							{(() => {
								const textAction = configuredActions.find((action) => action.type === 'Send Text');
								return textAction && textAction.items && textAction.items.length > 0 ? (
									<ConfigurationCard
										title="Send Text"
										icon={MessageCircle}
										iconColor="text-indigo-600"
										iconBgColor="bg-indigo-100"
										content={textAction.items}
										onCardClick={() => handleCardClick('Send Text')}
										emptyDescription="No texting workflows"
										isArrayContent
									/>
								) : null;
							})()}
							{/* display intake_questions_workflows too */}
							{(safeJSONParse(selectedAgent.intake_questions_workflows, []).length > 0 ||
								safeJSONParse(selectedAgent.intake_questions, []).length > 0) && (
								<ConfigurationCard
									title="Intake Questions"
									icon={FileText}
									iconColor="text-orange-600"
									iconBgColor="bg-orange-100"
									content={
										safeJSONParse(selectedAgent.intake_questions_workflows, []).length > 0
											? safeJSONParse(selectedAgent.intake_questions_workflows, [])
											: safeJSONParse(selectedAgent.intake_questions, [])
									}
									onCardClick={() => handleCardClick('Intake Questions')}
									emptyDescription="No intake questions"
									isArrayContent
								/>
							)}

							{/* Merged Calendar Card - Schedule an Appointment & Booking */}
							{(() => {
								const getTextBookingWorkflows = () => {
									try {
										const textWorkflows = selectedAgent?.workflows
											? (() => {
													try {
														return JSON.parse(selectedAgent.workflows as unknown as string);
													} catch (_parseError) {
														return [];
													}
												})()
											: [];

										if (Array.isArray(textWorkflows) && textWorkflows.length > 0) {
											return textWorkflows.filter((w: any) => w.bookingText === true);
										}
										return [];
									} catch (_error) {
										return [];
									}
								};

								const getBookingRequestWorkflows = () => {
									if (!selectedAgent?.intake_questions_workflows) {
										return [];
									}
									try {
										const intakeWorkflows = JSON.parse(
											selectedAgent.intake_questions_workflows || '[]',
										);
										return intakeWorkflows.filter((w: any) => w.bookingRequest === true);
									} catch (_error) {
										return [];
									}
								};

								const textBookingWorkflows = getTextBookingWorkflows();
								const bookingRequestWorkflows = getBookingRequestWorkflows();
								const calendars = calendarData?.data?.calendars || [];

								const hasCalendarContent = calendars.length > 0;
								const hasBookingWorkflows =
									textBookingWorkflows.length > 0 || bookingRequestWorkflows.length > 0;

								if (hasCalendarContent || hasBookingWorkflows) {
									const allCalendarItems: string[] = [];

									if (hasCalendarContent) {
										const calendarItems = calendars
											.map((calendar: any) => {
												const location =
													calendar.location || calendar.locationType || 'Go High Level';
												return [
													`Location: ${
														location === 'integrations:google:meet'
															? 'Google Meet'
															: location === 'integrations:zoom'
																? 'Zoom'
																: location === 'integrations:daily'
																	? 'Cal.com'
																	: location?.split(':').pop() || location
													}`,
													`Scenario: ${calendar.scenario}`,
												];
											})
											.flat();

										allCalendarItems.push(...calendarItems);
									}

									if (hasBookingWorkflows) {
										if (textBookingWorkflows.length > 0) {
											textBookingWorkflows.forEach((workflow: any, index: number) => {
												allCalendarItems.push(
													`Booking: ${workflow.description || `Scenario ${index + 1}`}`,
												);
											});
										}

										if (bookingRequestWorkflows.length > 0) {
											bookingRequestWorkflows.forEach((workflow: any, index: number) => {
												allCalendarItems.push(
													`Booking: ${workflow.customScenario || `Scenario ${index + 1}`}`,
												);
											});
										}
									}

									return (
										<ConfigurationCard
											title="Schedule an Appointment"
											icon={Calendar}
											iconColor="text-blue-600"
											iconBgColor="bg-blue-100"
											content={allCalendarItems}
											onCardClick={() => handleCardClick('Schedule an Appointment')}
											emptyDescription="No calendar or booking configuration"
											isArrayContent
										/>
									);
								}

								return <></>;
							})()}

							{/* Common Questions Card */}
							{selectedAgent?.sample_qa && (
								<ConfigurationCard
									title="Common Questions"
									icon={HelpCircle}
									iconColor="text-blue-600"
									iconBgColor="bg-blue-100"
									content={selectedAgent.sample_qa}
									onCardClick={() => handleCardClick('Common Questions')}
									emptyDescription="No common questions"
								/>
							)}
							{/* Post Call Webhook Card */}
							{selectedAgent?.postCallWebhook && (
								<ConfigurationCard
									title="Post Call Webhook"
									icon={Webhook}
									iconColor="text-purple-600"
									iconBgColor="bg-purple-100"
									content={[
										safeJSONParse(selectedAgent.postCallWebhook, {}).method,
										' ',
										safeJSONParse(selectedAgent.postCallWebhook, {}).endpoint,
									]}
									onCardClick={() => handleCardClick('Post Call Webhook')}
									emptyDescription="No post call webhook"
								/>
							)}

							{/* API Workflow Card */}
							{selectedAgent?.call_webhook_workflows &&
								safeJSONParse(selectedAgent.call_webhook_workflows, []).length > 0 && (
									<ConfigurationCard
										title="API Integration"
										icon={Code}
										iconColor="text-teal-600"
										iconBgColor="bg-teal-100"
										content={(() => {
											try {
												const parsedApiWorkflows = safeJSONParse(
													selectedAgent.call_webhook_workflows,
													[],
												);
												if (Array.isArray(parsedApiWorkflows)) {
													return parsedApiWorkflows.map((workflow: any) => {
														return workflow.name || workflow.scenario || 'Untitled API Workflow';
													});
												}
												return [];
											} catch (error) {
												console.error('Error parsing API workflows:', error);
												return [];
											}
										})()}
										onCardClick={() => handleCardClick('API Integration')}
										emptyDescription="No API workflows"
										isArrayContent
									/>
								)}
						</div>

						<div className="mt-4 sm:mt-6">
							<OverviewCard debrand={debrand} isNoviUser={false} onCompletionChange={() => {}} />
						</div>

						{/* Add test button in the detailed view when showTestBtn is true */}
						{showTestBtn && (
							<div className="test-button-container mt-6 text-center">
								<Button
									type="primary"
									onClick={onTest}
									icon={<PlayCircle size={20} />}
									className="test-button"
								>
									Test your changes →
								</Button>
							</div>
						)}
					</div>
				</motion.div>
			</div>
			{/* Floating Action Buttons */}
			<WorkflowIssuesFloatingButtons
				onCopilotClick={() => toggleDrawer(true)}
				showCopilot={
					generationsLeft > 0 &&
					(!clientHiddenFeatures.includes('Copilot') || currentReseller?.resellerView)
				}
			/>
		</div>
	);
};

export default AICustomizationStep;
